import { Elena, html } from "@elenajs/core";

/**
 * The <buggle-gallery> component.
 *
 * Basic usage:
 *
 * <buggle-gallery>
 *   <form><!-- optional --!>
 *     <label for="artist">Artist:</label>
 *     <select id="artist" name="artist">
 *       <option value="">All</option>
 *       <option value="Alice">Alice</option>
 *       <option value="Bob">Bob</option>
 *     </select>
 *   </form>
 *   <ul>
 *     <li data-artist="Alice"><img src="image/one.jpg" alt="First image"></li>
 *     <li data-artist="Alice,Bob"><img src="image/one.jpg" alt="First image"></li>
 *     <li data-artist="Bob"><img src="image/one.jpg" alt="First image"></li>
 *   </ul>
 * </buggle-gallery>
 *
 * Understand:
 *
 * 1. Your <buggle-gallery> MUST contain a <ul> or an <ol>; this MUST contain <li>s for each item in your gallery.
 * 2. What goes inside the <li>s is up to you; it can be images, links, text, whatever. If you're using images, be
 *    sure to add a loading="lazy" attribute to the <img> so they don't all load immediately!
 * 3. How you style your <li>s is also up to you. Some basic CSS to get you started is provided below.
 * 4. Optionally add a <form> to your <buggle-gallery> to allow users to filter and sort the gallery. (You can
 *    make the form invisible if you want to control the filters some other way, e.g. with links!) You might
 *    also like to make the form invisible to begin with and then make it visible using JavaScript, if you don't
 *    want non-JavaScript users to see it (because it won't work for them!).
 * 5. The <form> can contain any number of <select> and <input> fields to allow users to filter and search. Each
 *    must have a name="..." attribute that specifies what attribute it's searching on. E.g. name="medium" might
 *    be used to filter based on the medium of an artwork.
 * 6. Each <li> gets a data-... attribute that specifies what THAT ITEM's filter values are. E.g.
 *    data-medium="photograph" if it's a photograph. It's possible for am <li> to have multiple values for the
 *    same attribute, e.g. data-artist="Alice,Bob" for one that responds to filtering for the artist "Alice"
 *    or "Bob".
 * 7. The <form> can contain a <select> with the special name="sort" can be used to allow the gallery to be sorted.
 *    The <option value="..."> specifies the attribute to sort by. Optionally add data-sort-as="number" if the
 *    attribute is to be treated as a number (sorts numerically, not alphabetically), and/or add
 *    data-sort-direction="desc" for it to be a reverse-sort.
 * 8. Whenever the form is changed, the address bar will be updated to reflect the current state of the form.
 *    This WILL NOT WORK if you've got multiple <buggle-gallery>s on the page (don't do that!) and might break
 *    other components that rely on the address bar. If you need to disable the address bar functionality, use
 *    <buggle-gallery nohistory> ... </buggle-gallery> when you're creating the gallery! Assuming you've got
 *    it enabled, though, this means that users can bookmark or share links after searching/sorting.
 *
 * Sample starter CSS:
 * 
 * <style>
 *   buggle-gallery {
 *     &:not([hydrated]) form {
 *       display: none;
 *     }
 *
 *     ul {
 *       list-style: none;
 *       display: flex;
 *       flex-wrap: wrap;
 *       gap: 10px;
 *
 *       li {
 *         width: min-content;
 *         border: 2px solid #ccc;
 *         padding: 4px;
 *       }
 *     }
 *   }
 * </style>
 *
 */
export default class BuggleGallery extends Elena(HTMLElement) {
  static tagName = 'buggle-gallery';

  static props = ['nohistory'];
  nohistory = false; // if set to true, disables history/URL functionality

  connectedCallback() {
    super.connectedCallback?.();
    this.form = this.querySelector('form');
    this.form?.addEventListener('change', this._onFormChange);
    this.form?.addEventListener('keyup', this._onFormKeyup);
    this.list = this.querySelector('ol, ul');
    let originalPosition = 0;
    for(const li of Array.from(this.list.querySelectorAll('li'))) {
      li.dataset.originalposition = originalPosition++;
    }
    this._readHistory(); // initial load of state from address bar
    this._onFormChange(); // initial trigger
  }

  disconnectedCallback() {
    super.disconnectedCallback?.();
    this.form?.removeEventListener('change', this._onFormChange);
    this.form?.removeEventListener('keyup', this._onFormKeyup);
  }

  _doesLiMatch = (li, conditionKey, conditionValue) => {
    if(conditionValue === '') return true; // everything matches a null search!
    const liData = li.dataset;
    const liDataConditionValues = liData[conditionKey].split(',');
    for(const liDataConditionValue of liDataConditionValues) {
      if(liDataConditionValue.startsWith(conditionValue)) return true;
    }
    return false;
  }

  _readHistory = () => {
    if(this.nohistory) return;
    const url = new URL(window.location.href);
    const params = url.searchParams;
    for(const input of this.form.querySelectorAll('select, input:not([type="checkbox"]):not([type="radio"]), input:checked')) {
      if(input.tagName == 'SELECT') {
        const option = input.querySelector(`option[value="${params.get(input.name)}"]`);
        if(option) option.selected = true;
      } else {
        input.value = params.get(input.name);
      }
    }
  }

  _writeHistory = () => {
    if(this.nohistory) return;
    // write the names and values of all this.form's fields to the URL as params using the history api:
    const params = new URLSearchParams();
    for(const input of this.form.querySelectorAll('select, input:not([type="checkbox"]):not([type="radio"]), input:checked')) {
      if(input.value != '') params.set(input.name, input.value);
    }
    const paramsString = params.toString() == '' ? '' : `?${params.toString()}`;
    history.replaceState(null, '', `${window.location.pathname}${paramsString}`);
  }

  _onFormKeyup = (e) => {
    // only trigger change on keyup if within a field:
    if(e.target.closest('input,select')) this._onFormChange();
  }

  _onFormChange = () => {
    this._writeHistory();
    // Filtering:
    const lis = Array.from(this.list.querySelectorAll('li'));
    lis.forEach(li =>li.style.display = null);
    const filterFields = this.form.querySelectorAll('select:not([name="sort"]), input:not([type="checkbox"]):not([type="radio"]), input:checked');
    filterFields.forEach(input => {
      lis.forEach(li => {
        if (!this._doesLiMatch(li, input.name, input.value)) li.style.display = 'none';
      });
    });
    // Sorting:
    const sortField = this.form.querySelector('select[name="sort"]');
    if(!sortField) return; // no sort field? don't do sorting!
    const sortOption = sortField.querySelector('option:checked');
    const sortDirection = sortOption.dataset.sortDirection || 'asc';
    const sortAs = sortOption.dataset.sortAs || 'string'; // string, number
    const sortFieldValue = sortOption.value == '' ? 'originalposition' : sortOption.value;
    lis.sort((a, b) => {
      let aValue = a.dataset[sortFieldValue];
      let bValue = b.dataset[sortFieldValue];
      if(sortAs === 'number') {
        aValue = Number(aValue);
        bValue = Number(bValue);
      }
      if(aValue < bValue) return -1;
      if(aValue > bValue) return 1;
      return 0;
    });
    if(sortDirection === 'desc') lis.reverse();
    this.list.replaceChildren(...lis);
  }
}
BuggleGallery.define();
